import React, { useState, useEffect } from "react";
import "./App.css";
import { motion, AnimatePresence } from "framer-motion";
import {
  MapPin,
  Phone,
  Mail,
  Instagram,
  Facebook,
  X,
  Brain,
} from "lucide-react";
import DidimQuiz from "./DidimQuiz";

function App() {
  const [showGooglePopup, setShowGooglePopup] = useState(false);
  const [showWhatsAppPopup, setShowWhatsAppPopup] = useState(false);
  const [googleMapsClicked, setGoogleMapsClicked] = useState(false);
  const [showQuiz, setShowQuiz] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [popupsShown, setPopupsShown] = useState({
    google: false,
    whatsapp: false,
  });

  useEffect(() => {
    // Her sayfa yüklendiğinde popup göster
    console.log("Google popup 2 saniye sonra gösterilecek...");
    const timer = setTimeout(() => {
      console.log("Google popup gösteriliyor!");
      setShowGooglePopup(true);
      setPopupsShown((prev) => ({ ...prev, google: true }));
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    // WhatsApp popup'ı sadece Google'a tıklanmışsa göster
    const handleFocus = () => {
      if (googleMapsClicked && !showWhatsAppPopup && !popupsShown.whatsapp) {
        console.log("Kullanıcı geri döndü, WhatsApp popup gösteriliyor...");
        setTimeout(() => {
          setShowWhatsAppPopup(true);
          setPopupsShown((prev) => ({ ...prev, whatsapp: true }));
        }, 1000);
      }
    };

    if (googleMapsClicked && !popupsShown.whatsapp) {
      window.addEventListener("focus", handleFocus);

      return () => {
        window.removeEventListener("focus", handleFocus);
      };
    }
  }, [googleMapsClicked, showWhatsAppPopup, popupsShown.whatsapp]);

  const handleGoogleMapsClick = () => {
    console.log("Google Maps butonuna tıklandı!");
    setGoogleMapsClicked(true);
    setShowGooglePopup(false);
    window.open(
      "https://www.google.com/maps/place/Seahorse+Deluxe+Hotel/@37.3567626,27.3147708,17z/data=!3m1!4b1!4m9!3m8!1s0x14be87b5bf4c122b:0x10a057f0709f37a2!5m2!4m1!1i2!8m2!3d37.3567584!4d27.3173511!16s%2Fg%2F1tpb8963?entry=ttu&g_ep=EgoyMDI1MDgwMy4wIKXMDSoASAFQAw%3D%3D",
      "_blank"
    );
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(
      "Merhaba! Google Maps'e 5 yıldız verdim, bedava kahvemi alabilir miyim? ☕⭐ (5 yıldız yorumumun ekran görüntüsünü gönderiyorum)\n\nOda numaram: "
    );
    const whatsappUrl = `https://wa.me/905075061620?text=${message}`;
    window.open(whatsappUrl, "_blank");
    setShowWhatsAppPopup(false);
  };

  const closeGooglePopup = () => {
    setShowGooglePopup(false);
  };

  const closeWhatsAppPopup = () => {
    setShowWhatsAppPopup(false);
  };

  // Debug fonksiyonu - popup'ları sıfırla
  const resetPopups = () => {
    setShowGooglePopup(false);
    setShowWhatsAppPopup(false);
    setGoogleMapsClicked(false);
    setPopupsShown({ google: false, whatsapp: false });
    console.log("Popup'lar sıfırlandı!");
  };

  return (
    <div className="App">
      {/* Navigation */}
      <nav
        className={`navbar ${
          showGooglePopup || showWhatsAppPopup ? "navbar-hidden" : ""
        }`}
      >
        <div className="nav-container">
          <div className="nav-logo">
            <div className="seahorse-icon">🌊</div>
            <span>Seahorse</span>
          </div>
          <div className={`nav-links ${mobileMenuOpen ? "mobile-open" : ""}`}>
            <a href="#home" onClick={() => setMobileMenuOpen(false)}>
              Ana Sayfa
            </a>
            <a href="#free-coffee" onClick={() => setMobileMenuOpen(false)}>
              Bedava Kahve
            </a>
            <a href="#breakfast" onClick={() => setMobileMenuOpen(false)}>
              Kahvaltı
            </a>
            <a href="#amenities" onClick={() => setMobileMenuOpen(false)}>
              Olanaklar
            </a>
            <a href="#quiz" onClick={() => setMobileMenuOpen(false)}>
              Bilgi Yarışması
            </a>
            <a href="#contact" onClick={() => setMobileMenuOpen(false)}>
              İletişim
            </a>
          </div>
          <div
            className={`mobile-menu-toggle ${mobileMenuOpen ? "active" : ""}`}
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            <span></span>
            <span></span>
            <span></span>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section id="home" className="hero">
        {/* Seahorse Background Shapes */}
        <div className="seahorse-shape">🌊</div>
        <div className="seahorse-shape">🐚</div>
        <div className="seahorse-shape">🌊</div>
        <div className="seahorse-shape">🐚</div>
        <div className="seahorse-shape">🌊</div>
        <div className="seahorse-shape">🐚</div>
        <div className="seahorse-shape">🌊</div>
        <div className="seahorse-shape">🐚</div>

        <div className="hero-background">
          <div className="hero-waves"></div>
          <div className="floating-seahorses">
            <div className="seahorse seahorse-1">🐚</div>
            <div className="seahorse seahorse-2">🌊</div>
            <div className="seahorse seahorse-3">🐠</div>
          </div>
        </div>
        <div className="hero-content">
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1.2, ease: "easeOut" }}
            className="hero-text"
          >
            <div className="hero-badge">
              <span>✨ Premium Boutique Experience</span>
            </div>
            <h1>Seahorse Residences</h1>
            <p className="hero-subtitle">
              Denizatı Mimarisi ile Tasarlanmış Boutique Otel
            </p>
            <p className="hero-description">
              Denize sadece 400 metre mesafede, 5 farklı daire seçeneği ve 4
              çeşit havuz imkanı ile unutulmaz bir tatil deneyimi.
            </p>
            <div className="hero-buttons">
              <button
                className="btn-primary-new"
                onClick={() =>
                  window.open(
                    "https://wa.me/905075061620?text=" +
                      encodeURIComponent(
                        "Merhaba! Seahorse Residences için destek almak istiyorum.\n\nOda numaram: "
                      ),
                    "_blank"
                  )
                }
              >
                <span>Destek Al</span>
                <div className="btn-shine"></div>
              </button>
              <button
                className="btn-secondary-new"
                onClick={() =>
                  window.open(
                    "https://wa.me/905075061620?text=" +
                      encodeURIComponent(
                        "Merhaba! Seahorse Residences için talebim var.\n\nOda numaram: "
                      ),
                    "_blank"
                  )
                }
              >
                <Mail size={18} />
                <span>Talep İlet</span>
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Free Coffee Section */}
      <section id="free-coffee" className="free-coffee-section">
        {/* Seahorse decorations */}
        <div
          className="seahorse-shape"
          style={{
            top: "20%",
            right: "8%",
            fontSize: "2.5rem",
            opacity: "0.06",
          }}
        >
          ☕
        </div>
        <div
          className="seahorse-shape"
          style={{
            bottom: "15%",
            left: "5%",
            fontSize: "3rem",
            opacity: "0.06",
          }}
        >
          ⭐
        </div>

        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="free-coffee-content"
          >
            <div className="free-coffee-header">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="free-coffee-icon"
              >
                <div className="coffee-cup-large">☕</div>
                <div className="coffee-steam-large">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </motion.div>
              <h2>Bedava Kahveni Kap!</h2>
              <p className="free-coffee-subtitle">
                Google Maps'e 5 yıldızlı yorum yap, bedava kahveni al
              </p>
            </div>

            <div className="free-coffee-steps">
              <div className="coffee-step clickable-step" onClick={handleGoogleMapsClick}>
                <div className="step-number">1</div>
                <div className="step-content">
                  <h3>Google Maps'e Git</h3>
                  <p>Seahorse Deluxe Hotel'i bul</p>
                </div>
              </div>
              <div className="coffee-step">
                <div className="step-number">2</div>
                <div className="step-content">
                  <h3>5 Yıldız Ver</h3>
                  <p>Deneyimini paylaş</p>
                </div>
              </div>
              <div className="coffee-step clickable-step" onClick={handleWhatsAppClick}>
                <div className="step-number">3</div>
                <div className="step-content">
                  <h3>Screenshot Gönder</h3>
                  <p>WhatsApp'tan ekran görüntüsünü at</p>
                </div>
              </div>
              <div className="coffee-step">
                <div className="step-number">4</div>
                <div className="step-content">
                  <h3>Kahveni Al!</h3>
                  <p>Resepsiyondan bedava kahveni kap</p>
                </div>
              </div>
            </div>

            <div className="free-coffee-buttons">
              <motion.button
                className="btn-google-maps-section"
                onClick={handleGoogleMapsClick}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.4 }}
              >
                <div className="btn-content">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                  <span className="btn-text">Google Maps'e 5 Yıldız Ver</span>
                </div>
                <div className="btn-shine"></div>
              </motion.button>

              <motion.button
                className="btn-whatsapp-section"
                onClick={handleWhatsAppClick}
                whileHover={{ scale: 1.05, y: -2 }}
                whileTap={{ scale: 0.95 }}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                <div className="btn-content">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                  </svg>
                  <span className="btn-text">Screenshot Gönder</span>
                </div>
                <div className="btn-shine"></div>
              </motion.button>
            </div>


          </motion.div>
        </div>
      </section>

      {/* Breakfast Service Section */}
      <section id="breakfast" className="breakfast-service">
        {/* Seahorse decorations */}
        <div
          className="seahorse-shape"
          style={{
            top: "20%",
            right: "8%",
            fontSize: "2.5rem",
            opacity: "0.06",
          }}
        >
          🥐
        </div>
        <div
          className="seahorse-shape"
          style={{
            bottom: "15%",
            left: "5%",
            fontSize: "3rem",
            opacity: "0.06",
          }}
        >
          ☕
        </div>

        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="breakfast-content"
          >
            <div className="breakfast-header">
              <motion.div
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="breakfast-icon"
              >
                🍳
              </motion.div>
              <h2>Odaya Özel Kahvaltı Servisi</h2>
              <p className="breakfast-subtitle">
                Taze, lezzetli kahvaltınızı odanızın konforunda tadın
              </p>
            </div>

            <div className="breakfast-details">
              <div className="breakfast-features">
                <div className="breakfast-feature">
                  <span className="feature-icon">🏨</span>
                  <span>Odaya Servis</span>
                </div>
                <div className="breakfast-feature">
                  <span className="feature-icon">🕐</span>
                  <span>İstediğiniz Saatte</span>
                </div>
                <div className="breakfast-feature">
                  <span className="feature-icon">🥖</span>
                  <span>Taze Ürünler</span>
                </div>
                <div className="breakfast-feature">
                  <span className="feature-icon">👨‍🍳</span>
                  <span>Şef Hazırlığı</span>
                </div>
              </div>

              <div className="breakfast-price">
                <div className="price-tag">
                  <span className="price-amount">150₺</span>
                  <span className="price-unit">kişi başı</span>
                </div>
              </div>
            </div>

            <motion.button
              className="breakfast-order-btn"
              onClick={() =>
                window.open(
                  "https://wa.me/905075061620?text=" +
                    encodeURIComponent(
                      "Merhaba! Odaya kahvaltı servisi sipariş etmek istiyorum.\n\n📍 Oda numaram: \n👥 Kaç kişi: \n🕐 İstediğiniz saat: \n\n💰 Fiyat: 150₺ kişi başı"
                    ),
                  "_blank"
                )
              }
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
            >
              <div className="btn-content">
                <span className="btn-icon">📞</span>
                <span className="btn-text">Kahvaltı Sipariş Et</span>
              </div>
              <div className="btn-shine"></div>
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Amenities Section */}
      <section id="amenities" className="amenities">
        {/* Seahorse decorations */}
        <div
          className="seahorse-shape"
          style={{
            top: "10%",
            right: "5%",
            fontSize: "2.5rem",
            opacity: "0.06",
          }}
        >
          🌊
        </div>
        <div
          className="seahorse-shape"
          style={{
            bottom: "20%",
            left: "3%",
            fontSize: "3rem",
            opacity: "0.06",
          }}
        >
          🐚
        </div>

        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="section-header"
          >
            <h2>Özel Olanaklar</h2>
            <p>Tatilinizi unutulmaz kılacak premium hizmetler</p>
          </motion.div>
          <div className="amenities-grid">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="amenity-card"
            >
              <div className="amenity-icon">🏊‍♂️</div>
              <h3>4 Çeşit Havuz</h3>
              <p>Farklı derinlik ve özelliklerle 4 ayrı havuz deneyimi</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="amenity-card"
            >
              <div className="amenity-icon">🏠</div>
              <h3>5 Daire Seçeneği</h3>
              <p>Farklı büyüklük ve konforda 5 çeşit daire imkanı</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
              className="amenity-card"
            >
              <div className="amenity-icon">🌊</div>
              <h3>Denizatı Mimarisi</h3>
              <p>Otelimizin benzersiz denizatı temalı mimari tasarımı</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              className="amenity-card"
            >
              <div className="amenity-icon">🛒</div>
              <h3>Market & Bar</h3>
              <p>Otel içerisinde market ve bar hizmetleri</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.5 }}
              className="amenity-card"
            >
              <div className="amenity-icon">🏖️</div>
              <h3>400m Deniz Mesafesi</h3>
              <p>Denize sadece 400 metre yürüme mesafesinde konum</p>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.6 }}
              className="amenity-card"
            >
              <div className="amenity-icon">🚗</div>
              <h3>Altınkuma 5 Dakika</h3>
              <p>Ünlü Altınkum plajına arabayla sadece 5 dakika mesafede</p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quiz Section */}
      <section id="quiz" className="quiz-section">
        {/* Seahorse decorations */}
        <div
          className="seahorse-shape"
          style={{
            top: "15%",
            right: "8%",
            fontSize: "2.8rem",
            opacity: "0.06",
          }}
        >
          🐚
        </div>
        <div
          className="seahorse-shape"
          style={{
            bottom: "10%",
            left: "5%",
            fontSize: "3.2rem",
            opacity: "0.06",
          }}
        >
          🌊
        </div>

        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="quiz-section-content"
          >
            <div className="quiz-section-header">
              <h2>🏛️ Didim Bilgi Yarışması</h2>
              <p className="quiz-section-subtitle">
                Didim hakkında ne kadar bilgilisin? Eğlenceli sorularımızla
                kendini test et!
              </p>
              <p className="quiz-section-description">
                30 sorudan rastgele seçilen 5 soru ile Didim'in tarihi, kültürü
                ve güzelliklerini keşfet. Bilgini sına ve Didim uzmanı ol!
              </p>
            </div>

            <motion.button
              className="quiz-start-button"
              onClick={() => setShowQuiz(true)}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.95 }}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              <div className="quiz-button-content">
                <div className="quiz-button-icon">
                  <Brain size={24} />
                </div>
                <div className="quiz-button-text">
                  <span className="quiz-button-title">
                    Bilgi Yarışmasını Başlat
                  </span>
                  <span className="quiz-button-subtitle">
                    5 soru • 2-3 dakika
                  </span>
                </div>
              </div>
              <div className="quiz-button-shine"></div>
            </motion.button>

            <div className="quiz-features">
              <div className="quiz-feature">
                <span className="quiz-feature-icon">🎲</span>
                <span>Rastgele Sorular</span>
              </div>
              <div className="quiz-feature">
                <span className="quiz-feature-icon">⏱️</span>
                <span>Zamanlayıcı</span>
              </div>
              <div className="quiz-feature">
                <span className="quiz-feature-icon">📊</span>
                <span>Detaylı Sonuçlar</span>
              </div>
              <div className="quiz-feature">
                <span className="quiz-feature-icon">🏆</span>
                <span>Başarı Puanı</span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="contact">
        {/* Seahorse decorations */}
        <div
          className="seahorse-shape"
          style={{
            top: "15%",
            left: "8%",
            fontSize: "2.8rem",
            opacity: "0.06",
          }}
        >
          🐚
        </div>
        <div
          className="seahorse-shape"
          style={{
            bottom: "15%",
            right: "10%",
            fontSize: "3.2rem",
            opacity: "0.06",
          }}
        >
          🌊
        </div>

        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="contact-content"
          >
            <h2>İletişim</h2>
            <p className="contact-subtitle">Size nasıl yardımcı olabiliriz?</p>
            <div className="contact-grid">
              <div className="contact-card">
                <MapPin size={24} />
                <h3>Adres</h3>
                <p>
                  Altinkum Mahallesi, 217 Sokak, No: 1<br />
                  Gevrek Mevkii (Yesilkent)
                  <br />
                  09270 Didim / Aydın / Turkey
                </p>
              </div>
              <div className="contact-card">
                <Phone size={24} />
                <h3>Telefon</h3>
                <p>+90 507 506 16 20</p>
              </div>
              <div className="contact-card">
                <Mail size={24} />
                <h3>E-posta</h3>
                <p>reservation@seahorsedeluxehotel.com</p>
              </div>
            </div>
            <div className="social-section">
              <h3>Bizi Takip Edin</h3>
              <div className="social-links">
                <button className="social-link">
                  <Instagram size={20} />
                  <span>Instagram</span>
                </button>
                <button className="social-link">
                  <Facebook size={20} />
                  <span>Facebook</span>
                </button>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Google Maps Popup */}
      <AnimatePresence>
        {showGooglePopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="popup-overlay"
            onClick={closeGooglePopup}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="popup popup-google"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-coffee-beans"></div>
              <button className="popup-close" onClick={closeGooglePopup}>
                <X size={28} />
              </button>
              <div className="popup-content">
                <div className="popup-icon-coffee">
                  <div className="coffee-cup">☕</div>
                  <div className="coffee-steam">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
                <h3>Bedava Kahve Kazan!</h3>
                <div className="popup-message">
                  <p className="popup-offer">
                    ⭐ <strong>Google Maps'e 5 yıldız ver</strong>
                  </p>
                  <p>Kahven bizden olsun! 🎉</p>
                </div>
                <div className="popup-actions">
                  <button
                    className="btn-google-maps btn-large"
                    onClick={handleGoogleMapsClick}
                  >
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                    </svg>
                    5 Yıldız Ver & Kahve Kazan
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* WhatsApp Popup */}
      <AnimatePresence>
        {showWhatsAppPopup && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="popup-overlay"
            onClick={closeWhatsAppPopup}
          >
            <motion.div
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="popup popup-whatsapp"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="popup-coffee-beans"></div>
              <button className="popup-close" onClick={closeWhatsAppPopup}>
                <X size={28} />
              </button>
              <div className="popup-content">
                <div className="popup-icon-coffee">
                  <div className="coffee-cup ready">☕</div>
                  <div className="coffee-ready-glow"></div>
                </div>
                <h3>Kahven Hazır!</h3>
                <div className="popup-message">
                  <p className="popup-offer">
                    📸 <strong>Screenshot gönder, kahveni al!</strong>
                  </p>
                  <p>
                    Google Maps'teki 5 yıldız yorumunun ekran görüntüsünü gönder
                  </p>
                </div>
                <div className="popup-actions">
                  <button
                    className="btn-whatsapp btn-large"
                    onClick={handleWhatsAppClick}
                  >
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                    </svg>
                    Kahvemi Almaya Geliyorum
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Didim Quiz */}
      <DidimQuiz isOpen={showQuiz} onClose={() => setShowQuiz(false)} />
    </div>
  );
}

export default App;
